export * from './cookies';
export * from './template';
import { classifications } from './classifications';

export const parseHTMLContent = (
  content: string,
  json: Record<string, string>
) => {
  const parser = new DOMParser();
  const htmlContent = parser.parseFromString(content, "text/html");
  const deepestChildren = findDeepestChildren(htmlContent, "[data-ai-classification]");

  deepestChildren.forEach((deepestChild) => {
    const property = deepestChild.getAttribute("data-ai-classification");
    const text = deepestChild.textContent;
    let highestParentElement = deepestChild.closest("[data-ai-classification]");
    let currentElement = highestParentElement;

    while (currentElement.parentElement && currentElement.parentElement.getAttribute("data-ai-classification")) {
      currentElement = currentElement.parentElement;
      highestParentElement = currentElement;
    }

    const customText = json[ property ] || text;
    const node = handleHighestParentElement(highestParentElement, customText);
    highestParentElement.parentNode.replaceChild(node, highestParentElement);
  });

  removeUnusedClassifications(htmlContent);
  return serializeHTML(htmlContent.body);
};

function findDeepestChildren(element, selector) {
  const elements = element.querySelectorAll(selector);
  let deepestChildren = [];

  for (let i = 0; i < elements.length; i++) {
    const el = elements[ i ];
    const childElements = el.querySelectorAll(selector);
    if (childElements.length === 0) {
      deepestChildren.push(el);
    }
  }

  return deepestChildren;
}

function serializeHTML(element) {
  return element.innerHTML
    .replace(/\{\{\{([^}]+)\}\}\}/g, '<span contenteditable="false" class="fast-versioning">{{{$1}}}</span>')
    .replace(/\{\{([^}]+)\}\}/g, '<span contenteditable="false" class="fast-versioning">{{$1}}</span>');
}



function handleHighestParentElement(element: HTMLElement, textContent: string) {
  const content = element.outerHTML;
  const parser = new DOMParser();
  const parsedContent = parser.parseFromString(content, "text/html");
  removeEmptyTextNodes(parsedContent.body)
  const child = removeUnusedClassifications(parsedContent);
  child.textContent = textContent;
  const parsed = parsedContent.body.firstChild;
  return parsed.firstChild;
};

function removeEmptyTextNodes(node: ChildNode) {
  let child = node.firstChild;
  while (child) {
    let nextChild = child.nextSibling;
    if (child.nodeType === 3 && !/\S/.test(child.nodeValue)) node.removeChild(child);
    else if (child.nodeType === 1) removeEmptyTextNodes(child);
    child = nextChild;
  }
}

function removeUnusedClassifications(html: Document) {
  let child = html.body.firstChild;
  while (child.firstChild && child.firstChild.nodeType !== 3){
    let currentChild = child.firstChild;
    if(currentChild instanceof HTMLElement && currentChild.getAttribute("data-ai-classification")) {
      child.insertBefore(currentChild.firstChild, currentChild);
      child.removeChild(currentChild);
      currentChild = child.firstChild;
    }
    child = currentChild;
  }

  return child;
}


export const updateCollapsibleText = (
  data: Record<string, any>,
  json: Record<string, string>,
) => {
  return { ...data, content: parseHTMLContent(data.content, json) }
};

export const updateIcon = (
  data: Record<string, any>,
  json: Record<string, string>,
  icons: { line: Record<string, any>[], fill: Record<string, any> }
) => {
  const { AIClassificationAnchor } = data;
  let icon: string = data[ "icon" ];
  if (json[ AIClassificationAnchor ]) {
    const aiIcon = json[ AIClassificationAnchor ];
    let isIconInLibrary: boolean = false;
    if (aiIcon.endsWith('-line')) {
      const line = icons.line;
      isIconInLibrary = !!line.find((icon: any) => icon.icon === aiIcon);
    } else if (aiIcon.endsWith('-fill')) {
      const fill = icons.fill;
      isIconInLibrary = !!fill.find((icon: any) => icon.icon === aiIcon);
    }
    if (isIconInLibrary) icon = aiIcon;
  }
  return { ...data, icon: icon, AIClassificationAnchor: '' };
};

export const updateGrid = (
  data: Record<string, any>,
  json: Record<string, string>,
  resetAI = true
) => {

  const gridData = data.data;
  if (typeof gridData[ 0 ] === 'string') return { ...data, data: gridData };

  const mapped = gridData.map((item) => {
    if (!item.classification
      || !json[ item.classification ]
      || !json[ item.classification ].startsWith('http')
    ) return item;
    return { ...item, value: json[ item.classification ], classification: (!resetAI ? item.classification : '') }
  })
  return { ...data, data: mapped };
};

export const updateImage = (
  data: Record<string, any>,
  json: Record<string, string>,
  resetAI = true
) => {

  if (!data.AIClassificationAnchor
    || !json[ data.AIClassificationAnchor ]
    || !json[ data.AIClassificationAnchor ].startsWith('http')
    || json[ data.AIClassificationAnchor ].endsWith('.mp4')
  ) return data;

  return { ...data, src: json[ data.AIClassificationAnchor ], updatedSrc: json[ data.AIClassificationAnchor ], AIClassificationAnchor: (!resetAI ? data.AIClassificationAnchor : '') };
};

export const updateVideo = (
  data: Record<string, any>,
  json: Record<string, string>,
  resetAI = true
) => {

  if (!data.AIClassificationAnchor
    || !json[ data.AIClassificationAnchor ]
    || !json[ data.AIClassificationAnchor ].startsWith('http')
    || !json[ data.AIClassificationAnchor ].endsWith('.mp4')
  ) return data;

  return { ...data, src: json[ data.AIClassificationAnchor ], AIClassificationAnchor: (!resetAI ? data.AIClassificationAnchor : '') };
};

export const updateSlideBackground = (
  settings: Record<string, any>,
  json: Record<string, string>,
  resetAI = true
) => {

  if (!settings.AIClassificationAnchor
    || !json[ settings.AIClassificationAnchor ]
    || settings.backgroundType === 'backgroundColor'
    || !json[ settings.AIClassificationAnchor ].startsWith('http')
  ) return settings;


  if (settings.backgroundType) {
    const backgroundType = settings.backgroundType.toLowerCase();
    if (backgroundType === 'video') return { ...settings, backgroundVideo: json[ settings.AIClassificationAnchor ], AIClassificationAnchor: (!resetAI ? settings.AIClassificationAnchor : '') }
    if (backgroundType === 'image') return { ...settings, backgroundImage: json[ settings.AIClassificationAnchor ], AIClassificationAnchor: (!resetAI ? settings.AIClassificationAnchor : '') }
  }

  return settings;
};

export const updateTabs = (
  settings: Record<string, any>,
  json: Record<string, string>
) => {
  const { titles } = settings.tabsItems;
  const mapped = titles.map((item) => {
    const name = item.AIClassificationAnchor && json[ item.AIClassificationAnchor ]
      ? json[ item.AIClassificationAnchor ]
      : item.name;
   return {
      ...item,
      name,
      AIClassificationAnchor: ''
    }
  });

  return { ...settings, tabsItems: { ...settings.tabsItems, titles: mapped } };
};

export const updateSingleTabs = (
  settings: Record<string, any>,
  json: Record<string, string>,
  resetAI = true
) => {
  const name = settings.AIClassificationAnchor && json[ settings.AIClassificationAnchor ]
      ? json[ settings.AIClassificationAnchor ]
    : settings.tabTitle;

  if (resetAI) delete settings.AIClassificationAnchor;

  return { ...settings, tabTitle: name };
};



export const promptHeading = ({ type, company, website, product, industry }: Record<string, string>) => {
  const typePrompt = type ? ` for a ${type}` : '';
  const namePrompt = company ? `Company name is "${company}". ` : '';
  const websitePrompt = website ? `Company URL: "${website}". ` : '';
  const productPrompt = product ? `Company main product is "${product}". ` : '';
  const industryPrompt = industry ? `Industry: "${industry}". ` : '';

  return `ignore the last prompt.\n Return to me a json of a content${typePrompt}. ${namePrompt + websitePrompt + productPrompt + industryPrompt}Make the text business related\n\n`
}

export const isWebsiteValid = (url: string) => {
  const websiteRegex = new RegExp(/^(https?:\/\/)?(www\.)?[a-zA-Z0-9]+(\.[a-z]{2,}){1,3}(\/.*)?$/);
  return !!websiteRegex.exec(url)
}

export const userPromptHandler = (prompt: string) => {
  return `A user has sent me a request for a presentation in free text. The text is "${prompt} " I need you to extrapolate from that text and provide me with a JSON file containing the following parameters:\nPresentation subtype (json name "subtype"): This should be selected only from the given list of options (choose one).  Never ever choose any type that is not on the list. leave it empty if you don't have the data:\n"One pager"\n"Report"\n"Product pitch deck"\n"Sales deck"\n"General presentation"\n"Invest pitch deck"\n"Case study"\n"Sales proposal"\n"Brochure"\n"Project proposal"\n"Investor report"\n"Company presentation"\n"NGO Report"\nPresentation industry (json name "industry"): This should be selected only from the given list of options (choose one) leave it empty if you don't have the data:\n"Other",\n"Technology and Innovation",\n"Finance and Banking",\n"Consulting and Advisory",\n"Digital Marketing and Advertising",\n"SaaS and Software Tools",\n"Medical and Healthcare",\n"Apps and Mobile Technology",\n"E-commerce and Retail",\n"Real Estate and Property Management",\n"Agriculture and Farming",\n"Education and Training",\n"Manufacturing and Engineering",\n"Automotive and Transportation",\n"Construction and Infrastructure",\n"Energy and Utilities",\n"Logistics and Distribution",\n"Telecommunications and Networking",\n"Entertainment and Media",\n"Fashion and Apparel",\n"Non-profit and Social Services",\n"Government and Public Sector",\n"Health and Wellness",\n"Travel and Tourism",\n"Food and Beverage",\n"Environmental and Sustainability",\n"Sports and Fitness"\nName of the company or organization (optional): Add the name only if you have it from the user's input. json name "company"\nWebsite URL of the company or organization (optional): Add the URL only if you have it from the user's input or if you can find it. leave it empty if you don't have the data.  json name "website"\nThe product or service of the company and who is it for (optional): Add the product or service if you have it from the user's input or if you can guess it. leave it empty if you don't have the data.  json name "product"\nPlease provide the requested information in JSON format. if you don't have the data return it as "".`
}

export const getAiSubtypeFromUrl = (pathname: any) => {
  if (!pathname || typeof pathname !== 'string') return null;

  const domainToRemove = 'storydoc.com';
  const idx = pathname.indexOf(domainToRemove);
  if (idx !== -1) {
      pathname = pathname.substring(idx + domainToRemove.length);
  }

  for (const name in classifications) {
    const { urlDetectionRegex } = classifications[name]
    if (urlDetectionRegex && new RegExp(urlDetectionRegex, 'gi').test(pathname)) {
      return name;
    }
  }

  return null;
}

export const updateAnimatedNumbers = (
  settings: Record<string, any>,
  json: Record<string, string>,
  resetAI = true
) => {
  const classification = settings.aiClassification;
  if (!classification || !json[ classification ]) return settings;

  const value = json[ classification ];
  const match = value.match(/^([^0-9-]*)([-]?\d+(?:,\d{3})*(?:\.\d+)?)(.*)$/);

  if (!match) return settings;

  const [, prefix, number, suffix] = match;
  const cleanNumber = number.replace(/,/g, '');

  if (resetAI) delete settings.aiClassification;

  return {
    ...settings,
    number: cleanNumber,
    prefix,
    suffix,
  }
}

export const updateButton = (
  settings: Record<string, any>,
  json: Record<string, string>
) => {
  const {
    urlClassification = '',
    titleClassification = '',
    titleClassificationSecondary = '',
    urlClassificationSecondary = '',
    ...restSettings
  } = settings;

  const url = json[ urlClassification ] || settings.url;
  const title = json[ titleClassification ] || settings.title;
  const titleSecondary = json[ titleClassificationSecondary ] || settings.titleSecondary;
  const urlSecondary = json[ urlClassificationSecondary ] || settings.urlSecondary;

  return { ...restSettings, url, title, titleSecondary, urlSecondary };
}

export const updateChart = (
  settings: Record<string, any>,
  json: Record<string, string>,
  resetAI = true
) => {
  const { data } = settings;
  const mapped = data.map((row: Record<string, unknown>[]) => {
    return row.map((cell: Record<string, unknown>) => {
      const classification = cell && typeof cell === 'object' && typeof cell.classification === 'string' ? cell.classification : '';

      if (!classification || !json[ classification ]) return cell;

      const isNumber = !isNaN(parseFloat(json[ classification ]));
      const value = isNumber ? String(json[ classification ]) : cell.value;

      if (resetAI) delete cell.classification;

      return { ...cell, value };
    });
  })

  return { ...settings, data: mapped };
};


export const updateChartOutlines = (
  settings: Record<string, any>,
  json: Record<string, string>,
  resetAI = true
) => {
  const { data } = settings;
  const mapped = data.map((row: Record<string, unknown>[], rowIndex: number) => {
    return row.map((cell: Record<string, unknown>, cellIndex: number) => {
      const { classification = '' } = cell && typeof cell === 'object' ? cell : {};
      
      if (!classification || typeof classification !== 'string') return cell;
      
      const jsonValue = json[classification];

      if (!jsonValue) return cell;

      const isTextOrHeader = classification.startsWith('text') || rowIndex === 0 || cellIndex === 0;
      const isNumeric = !isNaN(parseFloat(jsonValue));
      
      let value = cell.value;
      if (isTextOrHeader) value = jsonValue;
      else if (isNumeric) value = String(jsonValue);

      if (resetAI) delete cell.classification;

      return { ...cell, value };
    });
  })

  return { ...settings, data: mapped };
};
